import { Button, cx } from "@light-technology/components";
import { ArrowLeft, ArrowRight } from "@untitledui/icons";
import useEmblaCarousel from "embla-carousel-react";
import { useEffect, useState } from "react";
import type {
  PlaybookContentGuidePreview,
  PlaybookContentSection,
} from "../../state/api.types";
import { playbookSectionLink } from "../../utilities/links";
import { PlaybookGuideCard } from "./PlaybookGuideCard";

type PlaybookSectionProps = {
  section: PlaybookContentSection;
  guides: PlaybookContentGuidePreview[];
};

export const PlaybookSection = ({ section, guides }: PlaybookSectionProps) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: "start",
    slidesToScroll: "auto",
    duration: 0,
  });

  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);
  useEffect(() => {
    if (!emblaApi) return;
    const update = () => {
      setCanScrollPrev(emblaApi.canScrollPrev());
      setCanScrollNext(emblaApi.canScrollNext());
    };
    update();
    emblaApi.on("select", update);
    emblaApi.on("reInit", update);
    return () => {
      emblaApi.off("select", update);
      emblaApi.off("reInit", update);
    };
  }, [emblaApi]);

  return (
    <div className="flex flex-col gap-xl">
      <div>
        <p className="text-xl font-semibold text-primary">{section.title}</p>
        <p className="text-md text-tertiary">{section.tagline}</p>
      </div>

      <div ref={emblaRef} className="overflow-hidden pb-1">
        <div className="flex gap-4">
          {guides.map((guide) => (
            <div
              key={guide.slug}
              className={cx(
                "w-full flex-none",
                "sm:w-[calc((100%-16px)/2)] xl:w-[calc((100%-32px)/3)]",
              )}
            >
              <PlaybookGuideCard guide={guide} />
            </div>
          ))}
        </div>
      </div>

      <div className="flex items-center justify-between">
        <div className="flex gap-3">
          <Button
            color="secondary"
            onClick={() => emblaApi?.scrollPrev()}
            isDisabled={!canScrollPrev}
            aria-label="Previous guides"
            iconTrailing={<ArrowLeft className="size-5" />}
          />
          <Button
            color="secondary"
            onClick={() => emblaApi?.scrollNext()}
            isDisabled={!canScrollNext}
            aria-label="Next guides"
            iconTrailing={<ArrowRight className="size-5" />}
          />
        </div>
        <Button color="secondary" href={playbookSectionLink(section)}>
          View all
        </Button>
      </div>
    </div>
  );
};
